import React, { useState } from 'react';
import { Button, ClickAwayListener, Paper, Popper } from '@mui/material';

import { MaterialPropertyValuePair, PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { ComponentType, CsiResult, isTruss } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import {
  hasPricingPermission as hasPricingPermissionSelector,
  hasTrussAnalysisPermission as hasTrussAnalysisPermissionSelector,
} from '@paragon/login-tools';
import { DistanceField, NumericField } from '@paragon/material';
import { humanCase } from '@paragon/utilities';

import { RootState } from '../../../../store';
import { setComponentName } from '../../../../store/actions/editComponentActions';
import { updateComponentDesignQuantity } from '../../../../store/actions/projectActions';
import {
  canActiveComponentDesignQuantityBeEdited,
  getActiveComponentDesignQuantity,
} from '../../../../store/selectors/componentDesignQuantitySelectors';
import { getActiveAnalysisSet } from '../../../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { getLoadCasesForActiveComponentDesign } from '../../../../store/selectors/getLoadCasesForActiveComponentDesignSelector';
import { getAnalysisSettings as getAnalysisSettingsForComponentDesign } from '../../../../store/selectors/settingsDatabaseSelectors';
import { getPriceForActiveComponentDesign } from '../../../../store/selectors/stockLengthAndPriceSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import {
  setComponentPlies,
  setComponentSpacing,
  setComponentType,
} from '../../../../store/slices/componentDesignsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { formatDesignCode } from '../settings/loading/DesignCodeField';
import { MaxDeflectionTableDisplay } from './MaxDeflectionTableDisplay';
import { MaximumBearingReactionsTable } from './MaximumBearingReactionsTable';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';

function getAnalysisSettings(state: RootState) {
  const componentDesign = getActiveComponentDesign(state);
  return componentDesign ? getAnalysisSettingsForComponentDesign(state, componentDesign.guid) : null;
}

export function ComponentDesignProperties() {
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const componentDesign = useAppSelector(getActiveComponentDesign);
  const analysisSettings = useAppSelector(getAnalysisSettings);
  const loadCases = useAppSelector(getLoadCasesForActiveComponentDesign);
  const set = useAppSelector(getActiveAnalysisSet);
  const priceInformation = useAppSelector(getPriceForActiveComponentDesign);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  const hasPricingPermission = useAppSelector(hasPricingPermissionSelector);
  const hasTrussAnalysisPermission = useAppSelector(hasTrussAnalysisPermissionSelector);
  const quantity = useAppSelector(getActiveComponentDesignQuantity);
  const canQuantityBeEdited = useAppSelector(canActiveComponentDesignQuantityBeEdited);
  const isInProjectContext = useAppSelector((state) => state.application.openContext?.type === 'project');

  const dispatch = useAppDispatch();

  const [maxBearingReactionsAnchorEl, setMaxBearingReactionsAnchorEl] = useState<HTMLElement | null>(null);
  const [maxDeflectionsAnchorEl, setMaxDeflectionsAnchorEl] = useState<HTMLElement | null>(null);

  if (!componentDesign) return null;

  let topChordCSI = '';
  let bottomChordCSI = '';
  let webCSI = '';
  let jsi = '';
  if (set && set.csi) {
    const formatCsi = (obj: CsiResult) =>
      obj !== null && loadCases[obj.loadCase - 1] !== undefined
        ? `${obj.csi.toFixed(2)} on Link ${obj.link}${
            obj.loadCase > 0 ? ` from LoadCase ${obj.loadCase}. ${loadCases[obj.loadCase - 1].name}` : ''
          }`
        : 'N/A';
    topChordCSI = formatCsi(set.csi.topChord);
    bottomChordCSI = formatCsi(set.csi.bottomChord);
    webCSI = formatCsi(set.csi.web);
    jsi = set.jsi;
  }

  let timeStamp = '';
  if (set && typeof set.timeStamp === 'string') {
    timeStamp = new Date(set.timeStamp).toString();
  }

  const spacing = isTruss(componentDesign.component) && (
    <DistanceField
      label="Spacing"
      disabled={isViewOnly}
      onSubmit={(spacing) => {
        dispatch(setComponentSpacing({ spacing, componentDesignGuid: componentDesign.guid }));
      }}
      value={componentDesign.component.spacing}
      min={componentDesign.component.numberOfPlies * componentDesign.component.thickness}
    />
  );

  const numberOfPlies = isTruss(componentDesign.component) && (
    <NumericField
      label="Plies"
      disabled={isViewOnly}
      onSubmit={(plies) => {
        dispatch(setComponentPlies({ plies, componentDesignGuid: componentDesign.guid }));
      }}
      value={componentDesign.component.numberOfPlies}
      min={1}
    />
  );

  // If analysis settings are not loaded, don't display any value because there could be a manual camber
  let camberValue = '';
  if (analysisSettings) {
    const camberNumber = analysisSettings.useManualCamber
      ? analysisSettings.manualCamber
      : (set?.calculatedCamber ?? 0);
    camberValue =
      camberNumber === 0 ? 'N/A' : `${analysisSettings.useManualCamber ? '*' : ''}${distanceFormatter(camberNumber)}`;
  }

  return (
    <PropertiesPanel
      title="Component Properties"
      titleButton={<PropertiesPanelMenuButton type="componentDesign" guid={componentDesign.guid} />}
    >
      <PropertyTextInput
        id="component-name"
        property="Name"
        disabled={isViewOnly}
        submit={(value) => {
          dispatch(setComponentName(value, componentDesign.guid));
        }}
        value={componentDesign.component.name}
        required
      />

      <PropertySelectField<ComponentType>
        property="Component Type"
        value={componentDesign.component.componentType}
        options={['RoofTruss', 'FloorTruss']}
        disabledOptions={['Wall']}
        getName={humanCase}
        submit={(newComponentType) => {
          dispatch(
            setComponentType({
              componentDesignGuid: componentDesign.guid,
              componentType: newComponentType,
            }),
          );
        }}
        disabled={isViewOnly || componentDesign.component.definitionType !== 'FeaturePattern'}
      />

      <NumericField
        label="Quantity"
        value={quantity}
        min={1}
        onSubmit={(newQuantity) => dispatch(updateComponentDesignQuantity(componentDesign.guid, newQuantity))}
        disabled={isViewOnly || !isInProjectContext || !canQuantityBeEdited}
      />

      {hasPricingPermission && (
        <MaterialPropertyValuePair
          property="Total Price"
          value={
            priceInformation.priceUnavailableReason
              ? `${priceInformation.priceText} - ${priceInformation.priceUnavailableReason}`
              : priceInformation.priceText
          }
        />
      )}

      {spacing}
      {numberOfPlies}

      <MaterialPropertyValuePair property="Camber" value={set ? camberValue : ''} />

      <MaterialPropertyValuePair
        property="Design Code"
        multiline
        value={set ? formatDesignCode([set.loadSettings.buildingCode, set.analysisSettings.designStandard]) : ''}
      />

      {hasTrussAnalysisPermission && (
        <MaterialPropertyValuePair multiline property="Analysis Timestamp" value={timeStamp} />
      )}

      <MaterialPropertyValuePair multiline property="Maximum Top Chord CSI" value={topChordCSI} />
      <MaterialPropertyValuePair multiline property="Maximum Bottom Chord CSI" value={bottomChordCSI} />
      <MaterialPropertyValuePair multiline property="Maximum Web CSI" value={webCSI} />
      <MaterialPropertyValuePair multiline property="Maximum JSI" value={jsi} />

      {set && (
        <>
          <Button
            variant="text"
            onClick={(event) => {
              setMaxBearingReactionsAnchorEl(event.currentTarget);
            }}
          >
            Show Maximum Bearing Forces
          </Button>

          <Popper open={Boolean(maxBearingReactionsAnchorEl)} anchorEl={maxBearingReactionsAnchorEl}>
            <ClickAwayListener onClickAway={() => setMaxBearingReactionsAnchorEl(null)}>
              <Paper elevation={1}>
                <MaximumBearingReactionsTable
                  paperElevation={1}
                  maximumBearingReactions={set.maximumBearingReactions}
                />
              </Paper>
            </ClickAwayListener>
          </Popper>
        </>
      )}

      {set?.maxDeflectionTable && (
        <>
          <Button
            variant="text"
            onClick={(event) => {
              setMaxDeflectionsAnchorEl(event.currentTarget);
            }}
          >
            Show BC Maximum Deflections
          </Button>

          <Popper open={Boolean(maxDeflectionsAnchorEl)} anchorEl={maxDeflectionsAnchorEl}>
            <ClickAwayListener onClickAway={() => setMaxDeflectionsAnchorEl(null)}>
              <Paper elevation={1}>
                <MaxDeflectionTableDisplay paperElevation={1} value={set.maxDeflectionTable} />
              </Paper>
            </ClickAwayListener>
          </Popper>
        </>
      )}
    </PropertiesPanel>
  );
}
